import { isManifest, Manifest, ManifestUpdate } from './schema';

export type DeviceContext = {
  name?: string;
  hardwareRevision?: string;
  otaProtocol?: string;
};

export type UpdateCompatibility = 'compatible' | 'incompatible' | 'unknown';

export type UpdateEvaluation = {
  update: ManifestUpdate;
  compatibility: UpdateCompatibility;
  reasons: string[];
};

export async function loadManifest(url = './manifest.json'): Promise<Manifest> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Failed to load manifest (${response.status}).`);
  }
  const data = await response.json();
  if (!isManifest(data)) {
    throw new Error('Manifest schema is invalid.');
  }
  return data;
}

export function evaluateUpdateCompatibility(
  update: ManifestUpdate,
  device: DeviceContext | null
): UpdateEvaluation {
  const reasons: string[] = [];
  if (!device) {
    return {
      update,
      compatibility: 'unknown',
      reasons: ['Connect a device to check compatibility.'],
    };
  }

  let compatibility: UpdateCompatibility = 'compatible';
  const name = device.name ?? '';
  const compat = update.compat ?? {};

  if (compat.device_name_prefix && !name.startsWith(compat.device_name_prefix)) {
    compatibility = 'incompatible';
    reasons.push(`Device name does not match ${compat.device_name_prefix}.`);
  }

  if (compat.hw_revs && compat.hw_revs.length > 0) {
    if (!device.hardwareRevision) {
      if (compatibility !== 'incompatible') {
        compatibility = 'unknown';
      }
      reasons.push('Hardware revision not available from device.');
    } else if (!compat.hw_revs.includes(device.hardwareRevision)) {
      compatibility = 'incompatible';
      reasons.push(`Hardware revision ${device.hardwareRevision} is not supported.`);
    }
  }

  if (compat.ota_protocol) {
    if (!device.otaProtocol) {
      if (compatibility !== 'incompatible') {
        compatibility = 'unknown';
      }
      reasons.push('OTA protocol not provided.');
    } else if (compat.ota_protocol !== device.otaProtocol) {
      compatibility = 'incompatible';
      reasons.push(`OTA protocol mismatch (${compat.ota_protocol}).`);
    }
  }

  return { update, compatibility, reasons };
}

export function evaluateManifest(
  manifest: Manifest,
  device: DeviceContext | null
): UpdateEvaluation[] {
  return manifest.updates.map((update) => evaluateUpdateCompatibility(update, device));
}
